import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { paths } from '../schema';
import { api, call } from '../client';

/* Step 17: the organisation's own settings (Profile, Usage, Notifications); its logo and brand
   colour (set by Tekansa) come with GET /organisation and theme the app (Step 17c). */

export function useOrganisation(enabled = true) {
  return useQuery({
    queryKey: ['organisation'],
    queryFn: () => call(api.GET('/v1/organisation')),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export type OrganisationPatch =
  paths['/v1/organisation']['patch']['requestBody']['content']['application/json'];

export function useUpdateOrganisation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: OrganisationPatch) => call(api.PATCH('/v1/organisation', { body })),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ['organisation'] }),
        qc.invalidateQueries({ queryKey: ['me'] }),
      ]),
  });
}

export function useOrganisationUsage() {
  return useQuery({
    queryKey: ['organisation', 'usage'],
    queryFn: () => call(api.GET('/v1/organisation/usage')),
  });
}

export function useNotificationPrefs() {
  return useQuery({
    queryKey: ['me', 'notifications'],
    queryFn: () => call(api.GET('/v1/me/notifications')),
  });
}

export function useSaveNotificationPrefs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (prefs: Record<string, boolean>) =>
      call(api.PUT('/v1/me/notifications', { body: { prefs } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'notifications'] }),
  });
}
